import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Header/Header';
import Footer from '../../Footer/Footer';
import styles from './FarmsmojoFigma.module.css';

const FarmsmojoPortfolio = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [mode, setMode] = useState('web');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background Elements */}
      <div className={styles.backgroundElements}>
        <div className={`${styles.blob} ${styles.blob1}`} />
        <div className={`${styles.blob} ${styles.blob2}`} />
        <div className={`${styles.blob} ${styles.blob3}`} />
      </div>

      <div className={styles.mainContent}>
        {/* Hero Section */}
        <section id="overview" className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.Badge}>
              <span className={styles.badge}>
                UX/UI Design Case Study
              </span>
            </div>
            <h1 className={styles.heroTitle}>
              Farmsmojo <span className={styles.heroTitleGradient}>Restaurant</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Transforming farm-to-table dining experience through intuitive design, 
              seamless ordering, and delightful user interactions
            </p>
            
            {/* Color Palette */}
            <div className={styles.heroColorPalette}>
              {[
                { color: '#ea580c', name: 'Primary Orange' },
                { color: '#16a34a', name: 'Fresh Green' },
                { color: '#f59e0b', name: 'Warm Amber' },
                { color: '#1f2937', name: 'Neutral Gray' }
              ].map((color, idx) => (
                <div key={idx} className={styles.heroColorItem}>
                  <div className={styles.heroColorSwatch} style={{ backgroundColor: color.color }} />
                  <span className={styles.heroColorName}>{color.name}</span>
                </div>
              ))}
            </div>
            
            <div className={styles.heroStats}>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statValueOrange}`}>4 weeks</div>
                <div className={styles.statLabel}>Duration</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statValueGreen}`}>Figma</div>
                <div className={styles.statLabel}>Primary Tool</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statValueAmber}`}>End-to-End</div>
                <div className={styles.statLabel}>Design Role</div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className={styles.scrollIndicator}>
            <div className={styles.scrollMouse}>
              <div className={styles.scrollDot} />
            </div>
          </div>
        </section>

        {/* Problem Statement */}
        <section id="problem" className={styles.section}>
          <div className={styles.sectionGrid2}>
            <div className={styles.problemContent}>
              <h2 className={styles.problemTitle}>
                The Challenge
              </h2>
              <div className={styles.divider} />
              <p className={styles.problemText}>
                Restaurant customers were struggling with cluttered menus, confusing navigation, 
                and a fragmented ordering experience. Traditional restaurant interfaces failed to 
                convey the fresh, farm-to-table ethos while making it difficult for users to 
                discover dishes and complete orders efficiently.
              </p>
              
              <div className={styles.goalsList}>
                {[
                  { icon: '🎯', text: 'Simplify menu browsing and discovery' },
                  { icon: '⚡', text: 'Reduce checkout friction by 60%' },
                  { icon: '🌱', text: 'Highlight farm-fresh sourcing story' },
                  { icon: '📱', text: 'Create seamless mobile-first experience' }
                ].map((goal, idx) => (
                  <div key={idx} className={styles.goalItem}>
                    <span className={styles.goalIcon}>{goal.icon}</span>
                    <span className={styles.goalText}>{goal.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className={styles.metricsCard}>
                <div className={styles.metricsInner}>
                  <h3 className={styles.metricsTitle}>Key Metrics</h3>
                  <div>
                    <div className={styles.metricItem}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricLabel}>User Frustration</span>
                        <span className={`${styles.metricValue} ${styles.metricValueRed}`}>78%</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div className={`${styles.progressFill} ${styles.progressFillRed}`} style={{ width: '78%' }} />
                      </div>
                    </div>
                    <div className={styles.metricItem}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricLabel}>Cart Abandonment</span>
                        <span className={`${styles.metricValue} ${styles.metricValueOrange}`}>65%</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div className={`${styles.progressFill} ${styles.progressFillOrange}`} style={{ width: '65%' }} />
                      </div>
                    </div>
                    <div className={styles.metricItem}>
                      <div className={styles.metricHeader}>
                        <span className={styles.metricLabel}>Navigation Issues</span>
                        <span className={`${styles.metricValue} ${styles.metricValueAmber}`}>82%</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div className={`${styles.progressFill} ${styles.progressFillAmber}`} style={{ width: '82%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Research & User Flow */}
        <section id="research" className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Research & Discovery
          </h2>
          
          <div className={styles.sectionGrid3}>
            {[
              { title: 'Busy Professionals', desc: 'Need quick ordering during lunch breaks', color: 'orange' },
              { title: 'Health Conscious', desc: 'Want detailed ingredient information', color: 'green' },
              { title: 'Food Enthusiasts', desc: 'Seek unique farm-to-table experiences', color: 'amber' }
            ].map((persona, idx) => (
              <div key={idx} className={styles.personaCard}>
                <div className={`${styles.personaIcon} ${styles[`personaIcon${persona.color.charAt(0).toUpperCase() + persona.color.slice(1)}`]}`}>
                  {idx === 0 ? '👔' : idx === 1 ? '🥗' : '🍽️'}
                </div>
                <h3 className={styles.personaTitle}>{persona.title}</h3>
                <p className={styles.personaDesc}>{persona.desc}</p>
              </div>
            ))}
          </div>
          
        </section>

        {/* Design System */}
        <section id="design" className={styles.section}>
          {/* Key Screens */}
          <div>
            <h3 className={styles.sectionTitle}>Key Screens & Features</h3>
            
            <div className={styles.screensGrid}>
              {[
                { title: 'Homepage Hero', desc: 'Immersive hero section with farm imagery and clear CTA', features: ['Full-screen visuals', 'Animated elements', 'Quick menu access'] },
                { title: 'Dish Details', desc: 'Rich product pages with ingredients and farm sourcing', features: ['High-res images', 'Nutritional info', 'Customization options'] },
                { title: 'Booking Options & Smart Features', desc: 'Seamless date, time, and seat selection with intelligent availability management', features: ['Date & time picker, Seat / table selection', 'Guest count selection, Real-time availability updates', 'Preferred seating tags (Window, AC, Outdoor, etc.), Instant booking confirmation'] },
                { title: 'Checkout Flow', desc: 'Streamlined 3-step checkout with progress indicator', features: ['Guest checkout', 'Multiple payments', 'Order tracking'] }
              ].map((screen, idx) => (
                <div key={idx} className={styles.screenCard}>
                  <div className={`${styles.screenPreview} ${styles[`screenPreview${idx + 1}`]}`}>
                  </div>
                  <h4 className={styles.screenTitle}>{screen.title}</h4>
                  <p className={styles.screenDesc}>{screen.desc}</p>
                  <div className={styles.featuresList}>
                    {screen.features.map((feature, fidx) => (
                      <div key={fidx} className={styles.featureItem}>
                        <div className={styles.featureDot} />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Challenges & Solutions
          </h2>
          
          <div>
            {[
              { 
                challenge: 'Complex menu with 100+ items felt overwhelming',
                solution: 'Implemented smart categorization, filters, and visual hierarchy',
                icon: '📚'
              },
              { 
                challenge: 'Users couldn\'t find farm sourcing information easily',
                solution: 'Added dedicated farm story section and ingredient traceability',
                icon: '🌾'
              },
              { 
                challenge: 'Mobile checkout had 7 steps causing high abandonment',
                solution: 'Redesigned to 3-step flow with autofill and guest checkout',
                icon: '📱'
              }
            ].map((item, idx) => (
              <div key={idx} className={styles.challengeCard}>
                <div className={styles.challengeSection}>
                  <span className={styles.challengeIcon}>{item.icon}</span>
                  <div>
                    <h4 className={`${styles.challengeLabel} ${styles.challengeLabelRed}`}>Challenge</h4>
                    <p className={styles.challengeText}>{item.challenge}</p>
                  </div>
                </div>
                <div className={styles.challengeSection}>
                  <span className={styles.challengeIcon}>✅</span>
                  <div>
                    <h4 className={`${styles.challengeLabel} ${styles.challengeLabelGreen}`}>Solution</h4>
                    <p className={styles.challengeText}>{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcome" className={styles.section}>
          

          {/* Learnings */}
          <div className={styles.learningsCard}>
            <h3 className={styles.learningsTitle}>Key Learnings</h3>
            <div className={styles.learningsGrid}>
              {[
                'Mobile-first approach is crucial for restaurant apps',
                'Visual hierarchy dramatically improves menu navigation',
                'Storytelling through design builds emotional connection',
                'Reducing friction in checkout directly impacts revenue',
                'User testing early catches critical usability issues',
                'Animation enhances experience when purposeful'
              ].map((learning, idx) => (
                <div key={idx} className={styles.learningItem}>
                  <div className={styles.learningNumber}>
                    {idx + 1}
                  </div>
                  <p className={styles.learningText}>{learning}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaTitle}>
              View Full Design in Figma
            </h2>
            <p className={styles.ctaText}>
              Explore interactive prototypes, detailed wireframes, and complete design system
            </p>
            <a 
              href="https://www.figma.com/proto/BjvoetQ9jvewTNP2Zoof61/Farmsmojo-Resturant?page-id=0%3A1&node-id=13-66&starting-point-node-id=10%3A3&t=qqGqMI9rZPlkld4z-1"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaButton}
            >
              Open Figma Project →
            </a>
          </div>
        </section>

        {/* Get In Touch - Last Section */}
        <section className={styles.wantMoreSection}>
          <div className={styles.wantMoreInner}>
            <h2 className={styles.wantMoreTitle}>
              Want to see more?
            </h2>
            <p className={styles.wantMoreText}>
              Check out the full prototype and design system on Figma
            </p>
            <button className={styles.wantMoreButton} onClick={() => navigate('/contact')}>
              Get In Touch →
            </button>
          </div>
        </section>
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default FarmsmojoPortfolio;